/*------------------------------------------------------------------------------
   About      : "API Permissions" category tab — the backend Strapi/BFF feature
                 tree, with every ui.* row filtered out (owned by UiAccessPanel)

   Adapted from the pre-revamp EntityPermissionPanel: same tree/search/pending
   pattern, now scoped to one category and fed by selectApiPermissionForest so
   ui_registry and any uncategorized ui.* feature never appear here (design
   doc §1.4 — the two categories are never merged).

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  alpha,
} from '@mui/material';
import { Search, ChevronsUpDown, ChevronsDownUp } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { selectApiPermissionForest, RbacEntityType } from 'store/dataReducers/rbacSlice';
import { PermissionTreeNode as PermissionTreeNodeType, AnnotatedFeature } from 'types/apis/rbacInterfaces';
import { PermissionTreeNode } from './PermissionTreeNode';

const matchText = (value: string | undefined, term: string) =>
  !!value && value.toLowerCase().includes(term);

const matchesFeature = (feature: AnnotatedFeature, term: string) =>
  matchText(feature.label, term) || matchText(feature.endpoint, term) || matchText(feature.path, term);

const filterTree = (nodes: PermissionTreeNodeType[], term: string): PermissionTreeNodeType[] =>
  nodes.reduce<PermissionTreeNodeType[]>((acc, node) => {
    if (matchText(node.name, term)) {
      acc.push(node);
      return acc;
    }
    const children = filterTree(node.children || [], term);
    const features = (node.features || []).filter((f) => matchesFeature(f, term));
    if (children.length || features.length) acc.push({ ...node, children, features });
    return acc;
  }, []);

const collectGroupIds = (nodes: PermissionTreeNodeType[], acc: number[] = []): number[] => {
  nodes.forEach((node) => {
    acc.push(node.id);
    collectGroupIds(node.children || [], acc);
  });
  return acc;
};

const chipSx = { height: 18, fontSize: 11 } as const;

export interface ApiPermissionPanelProps {
  entityType: RbacEntityType;
  entityId: number | string;
  onSaved: () => void | Promise<void>;
}

export const ApiPermissionPanel = ({ entityType, entityId, onSaved }: ApiPermissionPanelProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();

  const forest = useSelector(selectApiPermissionForest);

  const [pending, setPending] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  // Forest loads async — collapse everything the first time it arrives, without
  // clobbering any expand/collapse the admin has since done by hand.
  const defaultCollapseApplied = useRef(false);
  useEffect(() => {
    if (defaultCollapseApplied.current || !forest) return;
    defaultCollapseApplied.current = true;
    setCollapsed(new Set(collectGroupIds(forest.groups || [])));
  }, [forest]);

  const setPendingChange = (key: string, grant: boolean, serverGranted: boolean) => {
    setPending((prev) => {
      const next = new Map(prev);
      if (grant === serverGranted) next.delete(key);
      else next.set(key, grant);
      return next;
    });
  };

  const handleToggleGroup = (groupId: number, grant: boolean, serverGranted: boolean) =>
    setPendingChange(`g:${groupId}`, grant, serverGranted);

  const handleToggleFeature = (featureId: number, grant: boolean, serverGranted: boolean) =>
    setPendingChange(`f:${featureId}`, grant, serverGranted);

  const handleDiscard = () => setPending(new Map());

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);

    const groupGrants: number[] = [];
    const groupRevokes: number[] = [];
    const featureGrants: number[] = [];
    const featureRevokes: number[] = [];
    pending.forEach((grant, key) => {
      const [kind, idStr] = key.split(':');
      const id = Number(idStr);
      if (kind === 'g') (grant ? groupGrants : groupRevokes).push(id);
      else (grant ? featureGrants : featureRevokes).push(id);
    });

    const errors: string[] = [];
    const collect = (resp: { success: boolean; error?: string }) => {
      if (!resp.success && resp.error) errors.push(resp.error);
    };

    if (entityType === 'role') {
      for (const groupId of groupGrants) collect(await RbacApis.grantRoleFeatureGroup(rc, entityId, groupId));
      for (const groupId of groupRevokes) collect(await RbacApis.revokeRoleFeatureGroup(rc, entityId, groupId));
      if (featureGrants.length) collect(await RbacApis.attachRoleFeatures(rc, entityId, featureGrants));
      if (featureRevokes.length) collect(await RbacApis.detachRoleFeatures(rc, entityId, featureRevokes));
    } else {
      for (const groupId of groupGrants) collect(await RbacApis.grantUserFeatureGroup(rc, entityId, groupId));
      for (const groupId of groupRevokes) collect(await RbacApis.revokeUserFeatureGroup(rc, entityId, groupId));
      for (const featureId of featureGrants) collect(await RbacApis.grantUserFeature(rc, entityId, featureId));
      for (const featureId of featureRevokes) collect(await RbacApis.revokeUserFeature(rc, entityId, featureId));
    }

    if (errors.length) toast.error(`${errors.length} change(s) failed: ${errors[0]}`);
    else toast.success('Permissions updated');

    setPending(new Map());
    await onSaved();
    setSaving(false);
  };

  const handleToggleExpand = (id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(collectGroupIds(forest?.groups || [])));

  const searchTerm = search.trim().toLowerCase();
  const searchActive = searchTerm.length > 0;

  const visibleGroups = useMemo(() => {
    const groups = forest?.groups || [];
    return searchActive ? filterTree(groups, searchTerm) : groups;
  }, [forest, searchTerm, searchActive]);

  const visibleUncategorized = useMemo(() => {
    const features = forest?.uncategorized_features || [];
    return searchActive ? features.filter((f) => matchesFeature(f, searchTerm)) : features;
  }, [forest, searchTerm, searchActive]);

  const noResults = searchActive && visibleGroups.length === 0 && visibleUncategorized.length === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search API permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Expand all">
          <IconButton size="small" onClick={expandAll}>
            <ChevronsUpDown size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Collapse all">
          <IconButton size="small" onClick={collapseAll}>
            <ChevronsDownUp size={16} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 1 }}>
        {!forest && <CircularProgress size={20} sx={{ m: 1 }} />}
        {forest && (
          <>
            {visibleGroups.map((node) => (
              <PermissionTreeNode
                key={node.id}
                node={node}
                depth={0}
                pending={pending}
                collapsed={collapsed}
                searchActive={searchActive}
                onToggleExpand={handleToggleExpand}
                onToggleGroup={handleToggleGroup}
                onToggleFeature={handleToggleFeature}
              />
            ))}

            {visibleUncategorized.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: visibleGroups.length ? 2 : 0, mb: 0.5 }}>
                  Ungrouped Permissions
                </Typography>
                {visibleUncategorized.map((feature) => {
                  const override = pending.get(`f:${feature.id}`);
                  const serverGranted = feature.grant_state === 'granted';
                  const checked = override !== undefined ? override : serverGranted;
                  const viaRole = !feature.granted_directly && !!feature.granted_via_role_id;
                  const featureIsPending = override !== undefined;
                  return (
                    <Box
                      key={feature.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                        ...(featureIsPending
                          ? { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.08) }
                          : {}),
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => handleToggleFeature(feature.id, e.target.checked, serverGranted)}
                      />
                      <Typography variant="body2">{feature.label || feature.endpoint}</Typography>
                      {viaRole && (
                        <Chip size="small" variant="outlined" label="via role" sx={{ ...chipSx, color: 'text.secondary' }} />
                      )}
                      {featureIsPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
                    </Box>
                  );
                })}
              </>
            )}

            {noResults && (
              <Typography variant="body2" color="text.secondary">
                No permissions match "{search.trim()}".
              </Typography>
            )}

            {!searchActive && visibleGroups.length === 0 && visibleUncategorized.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No API permissions defined yet.
              </Typography>
            )}
          </>
        )}
      </Box>

      {pending.size > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {pending.size} pending change{pending.size > 1 ? 's' : ''}
          </Typography>
          <Button size="small" onClick={handleDiscard} disabled={saving}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};

